import { useState, useRef, useCallback, type CSSProperties } from 'react';
import { useAnalytics } from '@/shared/features/analytics/presentation/hooks/useAnalytics';
import { useNavigate } from 'react-router-dom';
import happyShoppingIcon from '@/shared/assets/icons/happy-shopping.svg';
import layoutIcon from '@/shared/assets/icons/layout.svg';
import magniferIcon from '@/shared/assets/icons/Magnifer.svg';
import userIcon from '@/shared/assets/icons/user.svg';
import slidersIcon from '@/shared/assets/icons/sliders.svg';

import SearchModal from '@/features/search/presentation/components/SearchModal';
import { navigateToSearchPage } from '@/features/search/utils/searchNavigation';
import HamburgerMenu from '@/shared/features/menu/presentation/components/HamburgerMenu';
import { GoToTopFab } from '@/shared/widgets/GoToTopFab';

import { cn } from '@/shared/utils/cn';
import type { FloatingNavConfig } from '../../domain/models/NewHomePageData';
import { useFloatingNavigation } from '../hooks/useFloatingNavigation';
import { useIsMobile, useIsDesktop } from '@/core/hooks/useMediaQuery';
import { getApiBaseUrl } from '@/core/utils/apiUtils';
import { useRevealOnScrollUpWhile } from '@/core/hooks/useRevealOnScrollUpWhile';
import { useUserContext } from '@/shared/features/user-context/presentation/hooks/useUserContext';
import { useAppSelector, useAppDispatch } from '@/app/store/store';
import { openLoginPopup } from '@/app/store/loginUiSlice';
import {
  selectMobileRecommendedFilterStripStuck,
  selectDesktopRecommendedBrowseFilterStripStuck,
  selectRecommendedBrowseFiltersEnabled,
  bumpRecommendedBrowseFilterButton,
} from '@/app/store/newHomeUiSlice';

import {
  HOME_PAGE_FLOATING_NAV_BAR_CLICKED,
  homePageFloatingNavBarClickedEventProperties,
} from '../analytics/homePageFloatingNavBarClicked';
import { getActiveHomeSectionName } from '../../utils/activeHomeSection';
import { trackSearchedForProduct } from '@/features/search/constants';

interface FloatingNavigationProps {
  config: FloatingNavConfig;
}

const shadowMap: Record<string, string> = {
  sm: 'shadow-sm',
  md: 'shadow-md',
  lg: 'shadow-lg',
  xl: 'shadow-xl',
};

const MOBILE_STUCK_CHROME_MS = 560;
const MOBILE_STUCK_EASE = 'cubic-bezier(0.32, 0.72, 0, 1)';

function MobileNavIconLabel({ label, useLightIcons }: { label: string; useLightIcons: boolean }) {
  return (
    <span
      className={cn(
        'block max-w-[3.25rem] truncate text-center text-[9px] font-medium leading-none',
        useLightIcons ? 'text-gray-800' : 'text-white/90',
      )}
    >
      {label}
    </span>
  );
}

export default function FloatingNavigationUnified({ config }: FloatingNavigationProps) {
  const { analytics, isInitialized } = useAnalytics();
  const { currentState, stateConfig, transition } = useFloatingNavigation(config);

  const isMobile = useIsMobile();
  const isDesktop = useIsDesktop();
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const { userContext } = useUserContext();

  const mobileRecommendedStripStuck = useAppSelector(selectMobileRecommendedFilterStripStuck);
  const desktopRecommendedStripStuck = useAppSelector(selectDesktopRecommendedBrowseFilterStripStuck);
  const recommendedBrowseFiltersEnabled = useAppSelector(selectRecommendedBrowseFiltersEnabled);

  const [isSearchModalOpen, setIsSearchModalOpen] = useState(false);
  const [isMenuOpen, setIsMenuOpen] = useState(false);

  const pillContainerRef = useRef<HTMLDivElement>(null);

  const isHeaderFold = currentState === 'expandedCenter';
  const mobileStuck = isMobile && mobileRecommendedStripStuck;

  const handleOpenLogin = useCallback(
    (mode?: 'login' | 'signup') => {
      dispatch(openLoginPopup(mode === 'signup' ? 'signup' : 'otp'));
    },
    [dispatch],
  );

  const goOrder = useCallback(() => {
    if (config.cartShouldRefresh) window.location.assign('/order');
    else navigate('/order');
  }, [config.cartShouldRefresh, navigate]);

  const trackFloatingNavBarClick = useCallback(
    (buttonName: string) => {
      if (!isInitialized || !analytics) return;
      try {
        const sectionName = getActiveHomeSectionName();
        analytics.trackEvent(
          HOME_PAGE_FLOATING_NAV_BAR_CLICKED,
          homePageFloatingNavBarClickedEventProperties({ buttonName, sectionName }),
        );
      } catch (err) {
        console.error('Error tracking Home Page Floating Nav Bar Clicked:', err);
      }
    },
    [analytics, isInitialized],
  );

  const goMyAccount = useCallback(() => {
    let path: string;
    if (userContext?.isLoggedIn) {
      path = '/account.html';
    } else {
      const fromEnv = getApiBaseUrl();
      const redirectBase = (fromEnv && fromEnv !== '/' ? fromEnv : window.location.origin).replace(
        /\/+$/,
        '',
      );
      path = `/showLoginPage?redirectUrl=${encodeURIComponent(`${redirectBase}/`)}`;
    }
    if (config.profileShouldRefresh) window.location.assign(path);
    else navigate(path);
  }, [config.profileShouldRefresh, navigate, userContext?.isLoggedIn]);

  const goToTopEnabled =
    config.enabled && (mobileRecommendedStripStuck || desktopRecommendedStripStuck);
  const goToTopOpen = useRevealOnScrollUpWhile(goToTopEnabled);

  if (isMobile && !config.showInMobile) return null;
  if (!config.enabled) return null;

  if (isDesktop && !config.showInDesktop) {
    return (
      <GoToTopFab
        open={goToTopOpen}
        zIndexClassName="z-[54]"
        offsetBottomClassName="bottom-8"
      />
    );
  }

  const { style } = stateConfig;
  const { chrome } = config;
  const dur = transition.duration;
  const ease = transition.easing;

  const navIconStyle: CSSProperties = chrome.useLightIcons
    ? { filter: 'brightness(0)' }
    : { filter: 'brightness(0) invert(1)' };

  const showMenuBtn = isHeaderFold ? stateConfig.showMenu : true;
  const showCartBtn = isHeaderFold ? stateConfig.showCart : true;
  const showSearchBtn = isHeaderFold ? stateConfig.showSearch : true;

  // V6 pill width calculation
  const pillW = (() => {
    if (!isMobile) return '220px';
    if (mobileStuck) return '100vw';
    return 'min(280px, calc(100vw - 2rem - env(safe-area-inset-left, 0px) - env(safe-area-inset-right, 0px)))';
  })();

  const handleSearch = (query: string) => {
    const trimmed = query.trim();
    if (!trimmed) return;
    trackSearchedForProduct(analytics, trimmed);
    navigateToSearchPage(query, (url) => navigate(url, { replace: false }));
    setIsSearchModalOpen(false);
  };

  return (
    <>
      <GoToTopFab
        open={goToTopOpen}
        zIndexClassName="z-[54]"
        offsetBottomClassName="bottom-[calc(1rem+5.5rem+env(safe-area-inset-bottom,0px))] md:bottom-8"
      />

      {/* V6 Glass Pill — Center Floating to Bottom-Edge Docked Morph */}
      <div
        className={cn(
          'fixed left-0 right-0 z-50 flex justify-center',
          isMobile && mobileStuck ? 'bottom-0 max-md:pb-[env(safe-area-inset-bottom)]' : 'bottom-4',
          isMobile && 'will-change-transform motion-reduce:transform-none',
        )}
        style={
          isMobile
            ? {
                transition: `transform ${MOBILE_STUCK_CHROME_MS}ms ${MOBILE_STUCK_EASE}, opacity ${MOBILE_STUCK_CHROME_MS}ms ${MOBILE_STUCK_EASE}, bottom ${MOBILE_STUCK_CHROME_MS}ms ${MOBILE_STUCK_EASE}, padding-bottom ${MOBILE_STUCK_CHROME_MS}ms ${MOBILE_STUCK_EASE}`,
              }
            : undefined
        }
      >
        <div ref={pillContainerRef}>
          <div
            className={cn(
              'flex overflow-hidden shadow-lg',
              shadowMap[style.shadow] ?? 'shadow-lg',
              'items-stretch',
            )}
            style={{
              height: isMobile && mobileStuck ? '64px' : '56px',
              minHeight: isMobile && mobileStuck ? '64px' : '56px',
              width: pillW,
              opacity: 1,
              borderRadius: mobileStuck ? '24px 24px 0px 0px' : '50px',
              background: chrome.glassBackground,
              backdropFilter: 'blur(16px) saturate(180%)',
              WebkitBackdropFilter: 'blur(16px) saturate(180%)',
              border: chrome.glassPillBorderOpen,
              pointerEvents: 'auto',
              transform: 'translateX(0)',
              transition: `width ${dur}ms ${ease}, height ${dur}ms ${ease}, min-height ${dur}ms ${ease}, opacity ${dur}ms ${ease}, border-radius ${dur}ms ${ease}`,
            }}
          >
            <div
              className={cn(
                'flex items-center overflow-hidden transition-all',
                isMobile
                  ? 'w-full items-center justify-center gap-2 px-2 py-1.5'
                  : 'gap-[18px] px-5 py-1.5',
              )}
              style={{
                transitionDuration: `${dur}ms`,
                transitionTimingFunction: ease,
                transitionProperty: 'gap',
              }}
            >
              {showMenuBtn && (
                <button
                  type="button"
                  onClick={() => {
                    trackFloatingNavBarClick('More');
                    setIsMenuOpen(true);
                  }}
                  className="flex items-center justify-center shrink-0 min-w-0 flex-1 flex-col gap-1 h-12"
                  aria-label="More"
                >
                  <img src={layoutIcon} alt="" className="h-8 w-8 shrink-0" style={navIconStyle} />
                  {isMobile && <MobileNavIconLabel label="More" useLightIcons={chrome.useLightIcons} />}
                </button>
              )}

              {showCartBtn && (
                <button
                  type="button"
                  onClick={() => {
                    trackFloatingNavBarClick('Cart');
                    goOrder();
                  }}
                  className="flex items-center justify-center shrink-0 min-w-0 flex-1 flex-col gap-1 h-12 relative"
                  aria-label="Cart"
                >
                  <span className="relative inline-flex shrink-0">
                    <img src={happyShoppingIcon} alt="" className="h-8 w-8" style={navIconStyle} />
                    {(userContext?.cartCount ?? 0) > 0 && (
                      <span className="absolute -top-0.5 -right-1 flex h-3.5 min-w-[14px] items-center justify-center rounded-full bg-white/80 px-0.5 text-[9px] font-semibold text-gray-900">
                        {userContext?.cartCount}
                      </span>
                    )}
                  </span>
                  {isMobile && <MobileNavIconLabel label="Cart" useLightIcons={chrome.useLightIcons} />}
                </button>
              )}

              {showSearchBtn && (
                <button
                  type="button"
                  onClick={() => {
                    trackFloatingNavBarClick('Search');
                    setIsSearchModalOpen(true);
                  }}
                  className="flex items-center justify-center shrink-0 min-w-0 flex-1 flex-col gap-1 h-12"
                  aria-label="Search"
                >
                  <img src={magniferIcon} alt="" className="h-8 w-8 shrink-0" style={navIconStyle} />
                  {isMobile && <MobileNavIconLabel label="Search" useLightIcons={chrome.useLightIcons} />}
                </button>
              )}

              <button
                type="button"
                onClick={() => {
                  trackFloatingNavBarClick('Profile');
                  goMyAccount();
                }}
                className="flex items-center justify-center shrink-0 min-w-0 flex-1 flex-col gap-1 h-12"
                aria-label="Profile"
              >
                <img src={userIcon} alt="" className="h-8 w-8 shrink-0" style={navIconStyle} />
                {isMobile && <MobileNavIconLabel label="Profile" useLightIcons={chrome.useLightIcons} />}
              </button>

              {/* Revealable 5th Filters Icon */}
              <div
                className={cn(
                  'flex items-center justify-center transition-all overflow-hidden shrink-0 h-12',
                  mobileStuck
                    ? 'w-auto min-w-0 flex-1 opacity-100'
                    : 'w-0 min-w-0 flex-none opacity-0 pointer-events-none',
                )}
                style={{ transitionDuration: `${dur}ms`, transitionTimingFunction: ease }}
              >
                <button
                  type="button"
                  onClick={() => dispatch(bumpRecommendedBrowseFilterButton())}
                  className="flex min-w-0 w-full flex-1 flex-col items-center justify-center gap-1 disabled:opacity-40 h-12"
                  disabled={!recommendedBrowseFiltersEnabled}
                  aria-label="Filters"
                >
                  <img src={slidersIcon} alt="" className="h-8 w-8 shrink-0" style={navIconStyle} />
                  <MobileNavIconLabel label="Filters" useLightIcons={chrome.useLightIcons} />
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Search Modal */}
      <SearchModal
        isOpen={isSearchModalOpen}
        onClose={() => setIsSearchModalOpen(false)}
        onSearch={handleSearch}
      />

      {/* Hamburger Menu */}
      <HamburgerMenu
        isOpen={isMenuOpen}
        onClose={() => setIsMenuOpen(false)}
        onOpenLogin={handleOpenLogin}
      />
    </>
  );
}
